import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { StudentFee } from '../types';

// Date formatting utilities
export const formatDate = (date: string | Date, formatString: string = 'dd/MM/yyyy'): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Invalid Date';

    // Simple date formatting
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();

    return `${day}/${month}/${year}`;
  } catch (error) {
    return 'Invalid Date';
  }
};

export const formatDateTime = (date: string | Date): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Invalid Date';

    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (error) {
    return 'Invalid Date';
  }
};

export const getAge = (dateOfBirth: string): number => {
  try {
    const birthDate = new Date(dateOfBirth);
    if (isNaN(birthDate.getTime())) return 0;

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  } catch (error) {
    return 0;
  }
};

// Currency formatting
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatNumber = (number: number): string => {
  return new Intl.NumberFormat('en-IN').format(number);
};

// String utilities
export const generateId = (prefix: string): string => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000);
  return `${prefix}${timestamp}${random.toString().padStart(3, '0')}`;
};

export const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const generateReceiptNumber = (): string => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000);
  return `RCP${timestamp}${random.toString().padStart(3, '0')}`;
};

// PDF Generation utilities
export const generateReceipt = (
  studentName: string,
  studentId: string,
  amount: number,
  paymentMode: string,
  receiptNo: string,
  date: string,
  feeCategories: string[],
  transactionId?: string
): void => {
  const doc = new jsPDF();

  // Header
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Kaksha360 School', 105, 20, { align: 'center' });

  doc.setFontSize(16);
  doc.text('Fee Payment Receipt', 105, 30, { align: 'center' });

  // Receipt details
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');

  const details = [
    ['Receipt No:', receiptNo],
    ['Date:', formatDate(date)],
    ['Student Name:', studentName],
    ['Student ID:', studentId],
    ['Amount Paid:', formatCurrency(amount)],
    ['Payment Mode:', paymentMode],
    ...(transactionId ? [['Transaction ID:', transactionId]] : []),
    ['Fee Categories:', feeCategories.join(', ')],
  ];

  let yPosition = 50;
  details.forEach(([label, value]) => {
    doc.text(label, 20, yPosition);
    doc.text(value, 80, yPosition);
    yPosition += 10;
  });

  // Footer
  doc.setFontSize(10);
  doc.text('This is a computer-generated receipt.', 105, 250, { align: 'center' });
  doc.text('Thank you for your payment.', 105, 260, { align: 'center' });

  // Save the PDF
  doc.save(`receipt-${receiptNo}.pdf`);
};

export const generateSalarySlip = (
  teacherName: string,
  teacherId: string,
  month: number,
  year: number,
  amount: number,
  paymentDate: string,
  paymentMode: string,
  transactionId?: string
): void => {
  const doc = new jsPDF();

  // Header
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Kaksha360 School', 105, 20, { align: 'center' });

  doc.setFontSize(16);
  doc.text('Salary Slip', 105, 30, { align: 'center' });

  // Salary details
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const details = [
    ['Employee Name:', teacherName],
    ['Employee ID:', teacherId],
    ['Month/Year:', `${monthNames[month - 1]} ${year}`],
    ['Payment Date:', formatDate(paymentDate)],
    ['Gross Salary:', formatCurrency(amount)],
    ['Net Salary:', formatCurrency(amount)],
    ['Payment Mode:', paymentMode],
    ...(transactionId ? [['Transaction ID:', transactionId]] : []),
  ];

  let yPosition = 50;
  details.forEach(([label, value]) => {
    doc.text(label, 20, yPosition);
    doc.text(value, 80, yPosition);
    yPosition += 10;
  });

  // Footer
  doc.setFontSize(10);
  doc.text('This is a computer-generated salary slip.', 105, 250, { align: 'center' });

  // Save the PDF
  doc.save(`salary-slip-${teacherId}-${month}-${year}.pdf`);
};

export const generateInvoicePdf = (
  bill: {
    billNumber?: string;
    billType?: 'monthly' | 'admission' | 'annual' | 'custom';
    month?: string;
    description?: string;
    dueDate?: string;
    generatedAt?: string;
    className: string;
    academicYear: string;
    feeStructure: { category: string; amount: number }[];
    totalFees: number;
    paidAmount: number;
    pendingAmount: number;
    status: string;
    payments: { receiptNo: string; date: string; mode: string; amount: number }[];
  },
  student: { name: string; studentId: string; section?: string },
  billTitle: string
): jsPDF => {
  const doc = new jsPDF();

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Kaksha360 School', 105, 18, { align: 'center' });

  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text('Fee Invoice', 105, 27, { align: 'center' });

  doc.setDrawColor(200);
  doc.line(15, 33, 195, 33);

  doc.setFontSize(11);
  const leftDetails: [string, string][] = [
    ['Bill No:', bill.billNumber || '—'],
    ['Bill Title:', billTitle],
    ['Generated On:', bill.generatedAt ? formatDate(bill.generatedAt) : '—'],
    ['Due Date:', bill.dueDate ? formatDate(bill.dueDate) : '—']
  ];
  const rightDetails: [string, string][] = [
    ['Student Name:', student.name],
    ['Student ID:', student.studentId],
    ['Class:', `${bill.className}${student.section ? ` - ${student.section}` : ''}`],
    ['Academic Year:', bill.academicYear]
  ];

  let y = 42;
  leftDetails.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 15, y);
    doc.setFont('helvetica', 'normal');
    doc.text(value, 50, y);
    y += 7;
  });

  y = 42;
  rightDetails.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, 110, y);
    doc.setFont('helvetica', 'normal');
    doc.text(value, 148, y);
    y += 7;
  });

  let cursorY = 75;

  autoTable(doc, {
    startY: cursorY,
    head: [['Fee Category', 'Amount']],
    body: bill.feeStructure.map(c => [c.category, formatCurrency(c.amount)]),
    foot: [['Total', formatCurrency(bill.totalFees)]],
    theme: 'grid',
    headStyles: { fillColor: [226, 145, 109] },
    footStyles: { fillColor: [40, 34, 26], textColor: 255, fontStyle: 'bold' },
    margin: { left: 15, right: 15 }
  });

  cursorY = (doc as any).lastAutoTable.finalY + 8;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Total Billed:', 15, cursorY);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(bill.totalFees), 55, cursorY);

  doc.setFont('helvetica', 'bold');
  doc.text('Paid:', 110, cursorY);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(bill.paidAmount), 135, cursorY);

  cursorY += 7;
  doc.setFont('helvetica', 'bold');
  doc.text('Pending:', 15, cursorY);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(bill.pendingAmount), 55, cursorY);

  doc.setFont('helvetica', 'bold');
  doc.text('Status:', 110, cursorY);
  doc.setFont('helvetica', 'normal');
  doc.text(bill.status, 135, cursorY);

  cursorY += 12;

  if (bill.payments.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Payment History', 15, cursorY);
    autoTable(doc, {
      startY: cursorY + 4,
      head: [['Receipt No', 'Date', 'Mode', 'Amount']],
      body: [...bill.payments]
        .sort((a, b) => b.date.localeCompare(a.date))
        .map(p => [p.receiptNo, formatDate(p.date), p.mode, formatCurrency(p.amount)]),
      theme: 'grid',
      headStyles: { fillColor: [226, 145, 109] },
      margin: { left: 15, right: 15 }
    });
  }

  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text('This is a computer-generated invoice.', 105, 285, { align: 'center' });

  return doc;
};

// Bill display helpers — a student can have many bills (monthly per month, admission, annual, custom)
export const billTitle = (bill: Pick<StudentFee, 'billType' | 'month' | 'description'>): string => {
  const type = bill.billType || 'monthly';
  if (type === 'custom') return bill.description || 'Custom Bill';
  if (type === 'monthly' && bill.month) {
    const [y, m] = bill.month.split('-').map(Number);
    return y && m
      ? `Monthly — ${new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}`
      : `Monthly — ${bill.month}`;
  }
  if (type === 'monthly') return 'Monthly Fee';
  if (type === 'admission') return 'Admission Fee';
  return 'Annual Fee';
};

export const billTypeBadgeStyle = (bill: Pick<StudentFee, 'billType'>): string => {
  switch (bill.billType || 'monthly') {
    case 'admission':
      return 'bg-[#173322] text-[#8ff0b8]';
    case 'annual':
      return 'bg-[#332711] text-[#f9daa3]';
    case 'custom':
      return 'bg-[#241f36] text-[#d6bbfb]';
    default:
      return 'bg-[#16212b] text-[#bfdbfe]';
  }
};

export const billStatusBadgeStyle = (status: string): string => {
  switch (status) {
    case 'Paid':
      return 'bg-[#173322] text-[#8ff0b8]';
    case 'Partial':
      return 'bg-[#332711] text-[#f9daa3]';
    case 'Overdue':
      return 'bg-[#341a17] text-[#fbccc8]';
    default:
      return 'bg-[#16212b] text-[#bfdbfe]';
  }
};

// Excel export utilities
export const exportToExcel = (data: any[], filename: string, sheetName: string = 'Sheet1'): void => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

export const exportStudentsToExcel = (students: any[]): void => {
  const formattedData = students.map(student => ({
    'Student ID': student.studentId,
    'Name': student.name,
    'Date of Birth': formatDate(student.dateOfBirth),
    'Age': getAge(student.dateOfBirth),
    'Gender': student.gender,
    'Class': student.class,
    'Section': student.section || '',
    'Parent Name': student.parentName,
    'Parent Contact': student.parentContact,
    'Parent Email': student.parentEmail,
    'Address': student.address,
    'Admission Status': student.admissionStatus,
    'Admission Date': student.admissionDate ? formatDate(student.admissionDate) : '',
    'Created At': formatDate(student.createdAt),
  }));

  exportToExcel(formattedData, 'students-list', 'Students');
};

export const exportTeachersToExcel = (teachers: any[]): void => {
  const formattedData = teachers.map(teacher => ({
    'Teacher ID': teacher.teacherId,
    'Name': teacher.name,
    'Email': teacher.email,
    'Phone': teacher.phone,
    'Subject': teacher.subject,
    'Department': teacher.department || '',
    'Qualification': teacher.qualification,
    'Date of Joining': formatDate(teacher.dateOfJoining),
    'Salary': formatCurrency(teacher.salary),
    'Status': teacher.status,
    'Created At': formatDate(teacher.createdAt),
  }));

  exportToExcel(formattedData, 'teachers-list', 'Teachers');
};

export const exportFeesReportToExcel = (feesData: any[]): void => {
  const formattedData = feesData.map(fee => ({
    'Student ID': fee.studentId,
    'Student Name': fee.studentName || '',
    'Class': fee.className,
    'Academic Year': fee.academicYear,
    'Total Fees': formatCurrency(fee.totalFees),
    'Paid Amount': formatCurrency(fee.paidAmount),
    'Pending Amount': formatCurrency(fee.pendingAmount),
    'Status': fee.status,
    'Last Payment Date': fee.lastPaymentDate ? formatDate(fee.lastPaymentDate) : 'N/A',
  }));

  exportToExcel(formattedData, 'fees-report', 'Fees Report');
};

// File utilities
export const getFileExtension = (filename: string): string => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
};

export const isImageFile = (file: File): boolean => {
  const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
  return imageTypes.includes(file.type);
};

export const isPDFFile = (file: File): boolean => {
  return file.type === 'application/pdf';
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Search and filter utilities
export const searchInString = (searchTerm: string, text: string): boolean => {
  return text.toLowerCase().includes(searchTerm.toLowerCase());
};

export const filterStudents = (students: any[], searchTerm: string, filters: any = {}) => {
  let filtered = students;

  // Apply search
  if (searchTerm) {
    filtered = filtered.filter(student =>
      searchInString(searchTerm, student.name) ||
      searchInString(searchTerm, student.studentId) ||
      searchInString(searchTerm, student.parentName) ||
      searchInString(searchTerm, student.parentContact) ||
      searchInString(searchTerm, student.class)
    );
  }

  // Apply filters
  if (filters.class) {
    filtered = filtered.filter(student => student.class === filters.class);
  }

  if (filters.admissionStatus) {
    filtered = filtered.filter(student => student.admissionStatus === filters.admissionStatus);
  }

  if (filters.gender) {
    filtered = filtered.filter(student => student.gender === filters.gender);
  }

  return filtered;
};

export const filterTeachers = (teachers: any[], searchTerm: string, filters: any = {}) => {
  let filtered = teachers;

  // Apply search
  if (searchTerm) {
    filtered = filtered.filter(teacher =>
      searchInString(searchTerm, teacher.name) ||
      searchInString(searchTerm, teacher.teacherId) ||
      searchInString(searchTerm, teacher.email) ||
      searchInString(searchTerm, teacher.subject) ||
      searchInString(searchTerm, teacher.department || '')
    );
  }

  // Apply filters
  if (filters.subject) {
    filtered = filtered.filter(teacher => teacher.subject === filters.subject);
  }

  if (filters.department) {
    filtered = filtered.filter(teacher => teacher.department === filters.department);
  }

  if (filters.status) {
    filtered = filtered.filter(teacher => teacher.status === filters.status);
  }

  return filtered;
};

// Pagination utilities
export const paginate = <T>(items: T[], page: number, itemsPerPage: number): T[] => {
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return items.slice(startIndex, endIndex);
};

export const getTotalPages = (totalItems: number, itemsPerPage: number): number => {
  return Math.ceil(totalItems / itemsPerPage);
};

// Local storage utilities
export const saveToLocalStorage = (key: string, data: any): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export const getFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error reading from localStorage:', error);
    return defaultValue;
  }
};

export const removeFromLocalStorage = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};